import React, { memo, useRef, useEffect } from 'react';
import { X, TrendingUp, Sparkles } from 'lucide-react';
import logoSmall from '../NorthAI_Logo_Small_Zoomed.png';
import { sanitize } from '../utils/sanitize';

const AgentBriefing = ({
  briefingData,
  onClose,
  onAccept,
  onAcceptAll,
  onReject,
  isLeadInVisitList,
  loadingLeadId,
  isAcceptingAll,
  panelMode = false,
  mobileSheet = false,
  onLeadFocus,
  focusedLeadId,
}) => {
  const sheetRef = useRef(null);
  // Refs for each briefing card, keyed by lead id, used to scroll the
  // focused card into view when focus is driven from the map.
  const cardRefs = useRef({});

  useEffect(() => {
    if (focusedLeadId == null) return;
    const node = cardRefs.current[focusedLeadId];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusedLeadId]);

  // Swipe-down to close when rendered as mobile bottom sheet
  useEffect(() => {
    if (!mobileSheet) return;
    const el = sheetRef.current;
    if (!el) return;
    let startY = null;
    const onStart = (e) => { startY = e.touches[0].clientY; };
    const onEnd = (e) => {
      if (startY !== null && e.changedTouches[0].clientY - startY > 80) onClose();
      startY = null;
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [mobileSheet, onClose]);
  if (!briefingData || briefingData.length === 0) {
    return null;
  }

  const inner = (
    <div
      data-testid="agent-briefing"
      className={panelMode
      ? "flex-1 min-h-0 w-full overflow-hidden flex flex-col bg-gray-900"
      : "bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
    }>
        {/* Header — blue/purple brand gradient aligned with NavRail + LeadDetailPanel */}
        <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white overflow-hidden">
          {/* Subtle top sheen */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative flex-shrink-0">
                <img src={logoSmall} alt="NorthAI" className="w-10 h-10 object-cover rounded-full ring-2 ring-white/30" />
                <Sparkles className="w-4 h-4 text-yellow-300 absolute -top-1 -right-1 drop-shadow" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold tracking-tight truncate">Daily AI Briefing</h2>
                <p className="text-indigo-100 text-xs mt-0.5 font-medium">
                  {briefingData.length} {briefingData.length === 1 ? 'recommendation' : 'recommendations'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              title="Close briefing"
              data-testid="briefing-close"
              className="flex-shrink-0 text-white/80 hover:text-white hover:bg-white/15 p-2 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${panelMode ? 'bg-gray-900' : ''}`}>
          {briefingData.map((lead, idx) => {
            // Ensure lead has global_id for proper identification
            const leadGlobalId = lead.global_id || `lead_${lead.id}`;
            const isFocused = focusedLeadId === lead.id;
            const isClickable = typeof onLeadFocus === 'function';

            return (
              <div
                key={leadGlobalId}
                data-testid="briefing-card"
                data-lead-id={lead.id}
                data-lead-global-id={leadGlobalId}
                ref={(el) => {
                  if (el) cardRefs.current[lead.id] = el;
                  else delete cardRefs.current[lead.id];
                }}
                onClick={isClickable ? () => onLeadFocus(lead) : undefined}
                className={`rounded-xl p-6 border-2 relative transition-all ${
                  isClickable ? 'cursor-pointer' : ''
                } ${
                  isFocused
                    ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-gray-900'
                    : ''
                } ${
                  panelMode
                    ? `bg-gray-800 border-gray-700 ${loadingLeadId === lead.id ? 'opacity-50' : ''}`
                    : `bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 ${loadingLeadId === lead.id ? 'opacity-50' : ''}`
                }`}
              >
                {/* Numbered badge — ties card to matching numbered marker on the map */}
                <div
                  className={`absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 ${
                    isFocused ? 'ring-2 ring-purple-300' : ''
                  }`}
                  aria-hidden="true"
                >
                  {idx + 1}
                </div>
                {loadingLeadId === lead.id && (
                  <div className={`absolute inset-0 rounded-xl flex items-center justify-center z-10 ${
                    panelMode ? 'bg-gray-900/70' : 'bg-white/70'
                  }`}>
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
                  </div>
                )}

                {/* Lead Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold ${panelMode ? 'text-gray-100' : 'text-gray-900'}`}>{lead.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-sm ${panelMode ? 'text-gray-400' : 'text-gray-600'}`}>Smart Score:</span>
                      <div className="flex items-center gap-2">
                        <div className={`text-2xl font-bold ${panelMode ? 'text-purple-300' : 'text-purple-600'}`}>{Math.round(lead.score)}</div>
                        <div className={`flex-1 rounded-full h-2 w-24 ${panelMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div
                            className={`h-2 rounded-full ${lead.score >= 90 ? 'bg-red-500' :
                              lead.score >= 70 ? 'bg-orange-500' :
                                'bg-purple-500'
                              }`}
                            style={{ width: `${lead.score}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Why This Lead - Combined Rationale & Summary */}
                <div className={`rounded-lg p-4 mb-4 border ${panelMode ? 'bg-blue-900/20 border-blue-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`rounded-full p-2 flex-shrink-0 ${panelMode ? 'bg-blue-800' : 'bg-blue-100'}`}>
                      <TrendingUp className={`w-5 h-5 ${panelMode ? 'text-blue-300' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className={`font-semibold text-sm mb-2 ${panelMode ? 'text-blue-300' : 'text-blue-900'}`}>Why This Lead?</div>
                      {/* Show summary as the main content if available */}
                      {lead.summary ? (
                        <p className={`text-sm leading-relaxed ${panelMode ? 'text-blue-200' : 'text-blue-800'}`}>{sanitize(lead.summary)}</p>
                      ) : (
                        <p className={`text-sm leading-relaxed ${panelMode ? 'text-blue-200' : 'text-blue-800'}`}>{sanitize(lead.rationale)}</p>
                      )}
                      {/* GeoKG location-intelligence insight (why this lead) */}
                      {lead.geokg_insight && (
                        <div className={`flex items-start gap-2 mt-3 rounded-md px-3 py-2 text-xs ${panelMode ? 'bg-teal-900/30 text-teal-200 border border-teal-800' : 'bg-teal-50 text-teal-900 border border-teal-200'}`}>
                          <span className="flex-shrink-0">🗺️</span>
                          <span className="leading-relaxed">{sanitize(lead.geokg_insight)}</span>
                        </div>
                      )}
                      {/* Show key metrics as badges */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {lead.forecasted_sales && (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${panelMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'}`}>
                            💰 ¥{lead.forecasted_sales.toLocaleString()} potential
                          </span>
                        )}
                        {lead.opportunity_score && (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${panelMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                            📊 {Math.round(lead.opportunity_score * 100)}% opportunity
                          </span>
                        )}
                        {lead.priority && (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            panelMode
                              ? (lead.priority.toLowerCase() === 'high' ? 'bg-red-900/40 text-red-300' : lead.priority.toLowerCase() === 'medium' ? 'bg-yellow-900/40 text-yellow-300' : 'bg-gray-700 text-gray-300')
                              : (lead.priority.toLowerCase() === 'high' ? 'bg-red-100 text-red-800' : lead.priority.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800')
                            }`}>
                            {lead.priority.toLowerCase() === 'high' ? '🔥' : lead.priority.toLowerCase() === 'medium' ? '⚡' : '❄️'} {lead.priority} priority
                          </span>
                        )}
                        {lead.status && (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${panelMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                            📌 {lead.status.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Talking Points */}
                {lead.talking_points && (
                  <div className="space-y-3">
                    <div>
                      <div className={`font-semibold text-sm mb-1 ${panelMode ? 'text-gray-300' : 'text-gray-700'}`}>Talking Points</div>
                      {/* Formatted Talking Points */}
                      {(() => {
                        // Sanitize at source — covers all branches (intro, bullets, fallback)
                        const text = sanitize(lead.talking_points);
                        if (!text) return null;

                        // Check if it's the bulleted format
                        if (text.includes('*') || text.includes('•')) {
                          // Split by * or • but keep the items
                          // Logic: Split by " * " or start of string "*"
                          const parts = text.split(/(?:\s\*|\s•|^\*|^•)/).map(p => p.trim()).filter(Boolean);

                          // Usually the first part is the intro if it doesn't look like a bullet content
                          // specific heuristic: check if original string started with bullet
                          const startsWithBullet = text.trim().startsWith('*') || text.trim().startsWith('•');

                          let intro = null;
                          let points = parts;

                          if (!startsWithBullet && parts.length > 0) {
                            intro = parts[0];
                            points = parts.slice(1);
                          }

                          return (
                            <div className={`text-sm space-y-2 ${panelMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {intro && <p className={`mb-2 italic ${panelMode ? 'text-gray-200' : 'text-gray-700'}`}>{intro}</p>}
                              <ul className="space-y-2">
                                {points.map((part, index) => {
                                  // Clean up any remaining leading markers that might have persisted
                                  const cleanPart = part.trim().replace(/^[*•-]\s*/, '');

                                  if (!cleanPart) return null;

                                  // Check for bold pattern at start of line
                                  // Supports: **Text**: , Text**: , Text: , **Text**
                                  // Limit lookahead for colon to 50 chars to avoid false positives on long sentences
                                  const startMatch = cleanPart.match(/^(\*\*.*?\*\*|.*?\*\*|[^:]{1,50}:)(.*)/s);

                                  if (startMatch) {
                                    const rawHeader = startMatch[1];
                                    const rest = startMatch[2];
                                    // Clean any markdown stars from header
                                    const cleanHeader = rawHeader.replace(/\*\*/g, '');

                                    return (
                                      <li key={index} className="flex items-start gap-2">
                                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                        <div className="leading-relaxed">
                                          <strong className={`font-bold ${panelMode ? 'text-gray-100' : 'text-gray-900'}`}>{cleanHeader}</strong>{rest}
                                        </div>
                                      </li>
                                    );
                                  }

                                  // Fallback: Check for inline bolding **text**
                                  const boldParts = cleanPart.split(/(\*\*.*?\*\*)/g);

                                  return (
                                    <li key={index} className="flex items-start gap-2">
                                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                      <div className="leading-relaxed">
                                        {boldParts.map((subPart, i) => {
                                          if (subPart.startsWith('**') && subPart.endsWith('**')) {
                                            return <strong key={i} className={`font-bold ${panelMode ? 'text-gray-100' : 'text-gray-900'}`}>{subPart.slice(2, -2)}</strong>;
                                          }
                                          return subPart;
                                        })}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          );
                        }

                        return <p className={`text-sm ${panelMode ? 'text-gray-300' : 'text-gray-600'}`}>{text}</p>;
                      })()}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={(e) => { e.stopPropagation(); onReject(lead.id); }}
                    disabled={loadingLeadId !== null || isAcceptingAll}
                    data-testid="briefing-reject"
                    data-lead-id={lead.id}
                    className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${panelMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    👎 Reject
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onAccept(lead.id); }}
                    disabled={isLeadInVisitList(leadGlobalId) || loadingLeadId !== null || isAcceptingAll}
                    data-testid="briefing-accept"
                    data-lead-id={lead.id}
                    data-in-list={isLeadInVisitList(leadGlobalId) ? 'true' : 'false'}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg font-semibold shadow-md shadow-purple-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLeadInVisitList(leadGlobalId)
                      ? (lead.rationale?.includes('Overdue visit') ? '⚠️ Overdue — View Visits' : '✅ Added to List')
                      : '👍 Accept & Add'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className={`p-6 border-t space-y-3 ${panelMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          {/* Accept All — only show if there are unaccepted leads */}
          {onAcceptAll && briefingData.some(lead => !isLeadInVisitList(lead.global_id || `lead_${lead.id}`)) && (
            <button
              onClick={onAcceptAll}
              disabled={loadingLeadId !== null || isAcceptingAll}
              data-testid="briefing-accept-all"
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg font-semibold shadow-md shadow-purple-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAcceptingAll
                ? 'Adding to visit list...'
                : `Accept All (${briefingData.filter(lead => !isLeadInVisitList(lead.global_id || `lead_${lead.id}`)).length} remaining)`
              }
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="briefing-close-footer"
            className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
          >
            Close Briefing
          </button>
        </div>
      </div>
  );

  if (panelMode) return inner;

  if (mobileSheet) {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-[54]" onClick={onClose} />
        <div
          ref={sheetRef}
          className="fixed inset-x-0 bottom-0 z-[55] rounded-t-2xl overflow-hidden flex flex-col"
          style={{ height: '88vh', transition: 'transform 0.3s ease-out' }}
        >
          <div className="flex justify-center pt-3 pb-1 bg-gray-900 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-600" />
          </div>
          {inner}
        </div>
      </>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      {inner}
    </div>
  );
};

// Custom comparator: skip re-render unless props that affect the rendered output change.
// isLeadInVisitList and onAccept/onReject/onClose/onAcceptAll are function references —
// they will still cause a re-render when App.js recreates them, but we gate on the
// data-driven props so at minimum data-identical renders are skipped.
export default memo(AgentBriefing, (prev, next) => {
  return (
    prev.isOpen === next.isOpen &&
    JSON.stringify(prev.briefingData) === JSON.stringify(next.briefingData) &&
    prev.isLoading === next.isLoading &&
    prev.loadingLeadId === next.loadingLeadId &&
    prev.isAcceptingAll === next.isAcceptingAll &&
    prev.isLeadInVisitList === next.isLeadInVisitList &&
    prev.onClose === next.onClose &&
    prev.onAccept === next.onAccept &&
    prev.onAcceptAll === next.onAcceptAll &&
    prev.onReject === next.onReject &&
    prev.focusedLeadId === next.focusedLeadId &&
    prev.onLeadFocus === next.onLeadFocus &&
    prev.mobileSheet === next.mobileSheet
  );
});
